import { useConversation } from "@elevenlabs/react";
import MarkdownComponent from "components/Markdown";
import { useContext, useEffect, useRef, useState } from "react";
import useTranslation from "translations/hooks/useTranslation";
import SessionContext from "../session/SessionContext";

const AIChat = () => {
  const { t } = useTranslation();
  const { session } = useContext(SessionContext);

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const messagesEndRef = useRef(null);



  const conversation = useConversation({
    onConnect: () => {
      setIsConnected(true);
      setError(null);
    },
    onDisconnect: () => {
      setIsConnected(false);
    },
    onMessage: (message) => {
      const messageText =
        message.message || message.text || JSON.stringify(message);

      // Check if message contains navigation paths and is from agent
      if (message.source === "ai") {
        // Look for /s/ or /b/ patterns in the message
        const pathRegex = /\/[sb]\/[^\s.]+/g;
        const paths = messageText.match(pathRegex);

        if (paths && paths.length > 0) {
          // Navigate to the first path found using Next.js router
          location.href = `${session?.lang}${paths[0]}`;
          return; // Don't add this message to chat
        }
      }

      let isFirstMessage =
        messageText ===
        "Hi, thanks for contacting OEA Support! How can I help you today?";
      if (isFirstMessage && messages?.length > 0) return;

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: messageText,
          sender: message.source === "user" ? "user" : "agent",
          timestamp: new Date(),
        },
      ]);
    },
    onError: (error) => {
      console.error("ElevenLabs error:", error);
      setError(error?.message || "An error occurred");
    },
    textOnly: true, // Always use text-only mode
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages?.length]);

  // No need to prevent body scroll since it's not a modal anymore

  const startConversation = async () => {
    try {
      const agentId =
        process?.env?.NEXT_PUBLIC_ELEVENLABS_AGENT_ID ||
        "agent_6501k9rrqk0rec09c1wh7375qyk0";
      if (!agentId) {
        throw new Error(
          "Please set NEXT_PUBLIC_ELEVENLABS_AGENT_ID in your .env file"
        );
      }
      await conversation.startSession({
        agentId,
        connectionType: "websocket",
      });
    } catch (err) {
      console.error("Failed to start conversation:", err);
      setError(err.message);
    }
  };

  const endConversation = async () => {
    try {
      await conversation.endSession();
    } catch (err) {
      console.error("Failed to end conversation:", err);
      setError(err.message);
    }
  };

  const sendMessage = async () => {
    const trimmed = inputMessage.trim();
    if (!trimmed) return;

    // Add user message to UI immediately
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        text: trimmed,
        sender: "user",
        timestamp: new Date(),
      },
    ]);

    try {
      // Ensure a session exists
      if (!isConnected) {
        await startConversation();
      }
      conversation.sendUserMessage(trimmed);
    } catch (e) {
      console.error("Failed to send message:", e);
      setError("Failed to send message");
    }
    setInputMessage("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleTextareaChange = (e) => {
    setInputMessage(e.target.value);
    // Auto-resize textarea
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  // Start conversation when chat opens
  useEffect(() => {
    const maybeStart = async () => {
      if (!isChatOpen) return;
      if (isConnected) return;
      try {
        await startConversation();
      } catch (_) {}
    };
    maybeStart();
  }, [isChatOpen]);

  return (
    <div className="fixed bottom-5 ltr:right-5 rtl:left-5 z-[1000] flex flex-col items-end xs:bottom-3 xs:right-3 tablet:bottom-4 tablet:right-4">
      {isChatOpen && (
        <div className="mb-4 w-[95%] md:w-[550px] h-[600px] md:h-[600px] bg-white/95 backdrop-blur-[10px] rounded-[20px] shadow-[0_20px_40px_rgba(0,0,0,0.1)] flex flex-col overflow-hidden transition-all duration-300 ease-out transform origin-bottom-right animate-[scaleIn_0.3s_ease] border border-white/20 xs:rounded-[16px] xs:mb-3 tablet:rounded-[18px] tablet:mb-3">
            {/* Chat Header */}
            <div className="bg-gradient-to-br from-oeaMain to-[#76B2CC] text-white p-5 flex flex-col gap-4 xs:p-4 xs:gap-3 tablet:p-4 tablet:gap-3">
              <div className="flex justify-between items-center w-full">
                <h1 className="text-2xl font-semibold m-0 xs:text-xl tablet:text-xl">{t("oeaAIAssistant")}</h1>
                <button
                  onClick={async () => {
                    // End conversation when closing chat
                    if (isConnected) {
                      await endConversation();
                    }
                    setIsChatOpen(false);
                  }}
                  className="w-10 h-10 rounded-full bg-white/20 border border-white/30 text-white text-xl flex items-center justify-center hover:bg-white/30 hover:-translate-y-0.5 transition-all duration-300 xs:w-8 xs:h-8 xs:text-lg tablet:w-9 tablet:h-9"
                  aria-label="Close Chat"
                >
                  ×
                </button>
              </div>

              {/* Connection Status */}
              <div className="flex items-center gap-2 text-sm w-full xs:text-xs xs:gap-1.5 tablet:text-sm">
                <div
                  className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 xs:w-2 xs:h-2 ${
                    isConnected
                      ? "bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]"
                      : "bg-red-400"
                  }`}
                />
                <span>{isConnected ? t("connected") : t("disconnected")}</span>
                {/* <div className="ml-auto text-xs px-2.5 py-1 rounded-full border border-white/35 bg-white/20 text-white xs:px-2 xs:py-0.5 xs:text-[10px] tablet:text-xs">
                  Mode: Text Only
                </div> */}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 p-3 mx-4 mt-4 rounded-lg flex justify-between items-center animate-[slideDown_0.3s_ease] xs:p-2 xs:mx-3 xs:mt-3 xs:text-sm tablet:p-2.5 tablet:mx-3">
                <span className="xs:text-sm">⚠️ {error}</span>
                <button
                  onClick={() => setError(null)}
                  className="bg-none border-none text-xl cursor-pointer text-red-600 p-0 w-5 h-5 flex items-center justify-center xs:text-lg xs:w-4 xs:h-4"
                >
                  ×
                </button>
              </div>
            )}

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 scrollbar-thin scrollbar-track-lightGrey scrollbar-thumb-disabled hover:scrollbar-thumb-gray-400 xs:p-3 xs:gap-2 tablet:p-3">
              {messages.length === 0 ? (
                <div className="flex justify-center items-center h-full text-disabled text-lg text-center xs:text-base tablet:text-base">
                  {isConnected ? t("startConversation") : t("connectToStartChatting")}
                </div>
              ) : (
                messages?.map((message) => (
                  <div
                    key={message.id}
                    className={`flex mb-4 animate-[fadeInUp_0.3s_ease] xs:mb-3 tablet:mb-3 ${
                      message.sender === "user"
                        ? "justify-end"
                        : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[70%] p-3 rounded-[18px] relative break-words xs:max-w-[85%] xs:p-2.5 xs:rounded-[14px] tablet:max-w-[75%] tablet:p-2.5 tablet:rounded-[16px] ${
                        message.sender === "user"
                          ? "bg-gradient-to-br from-[#76B2CC] to-[#667eea] text-white rounded-br-[4px]"
                          : "bg-lightGrey text-black rounded-bl-[4px] border border-gray-200"
                      }`}
                    >
                      {/* <p className="m-0 leading-relaxed xs:text-sm xs:leading-normal tablet:text-sm">{message.text}</p> */}
                      <MarkdownComponent markdown={message?.text} />
                      <span className="text-xs opacity-70 block mt-1 xs:text-[10px] tablet:text-xs">
                        {message.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Container */}
            <div className="p-4 bg-lightGrey border-t border-gray-200 xs:p-3 tablet:p-3">
              <div className="flex gap-3 items-end xs:gap-2 tablet:gap-2.5">
                <textarea
                  value={inputMessage}
                  onChange={handleTextareaChange}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    isConnected
                      ? t("typeYourMessage")
                      : t("connectToStartChatting")
                  }
                  disabled={!isConnected}
                  rows="1"
                  className="flex-1 border-2 border-gray-200 rounded-xl text-base font-inherit resize-none min-h-[44px] max-h-[120px] transition-colors duration-300 bg-white pt-2.5 pl-2.5 focus:outline-none focus:border-[#667eea] focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)] disabled:bg-lightGrey disabled:text-disabled disabled:cursor-not-allowed xs:text-sm xs:min-h-[40px] xs:max-h-[100px] xs:pt-2 xs:pl-2 xs:rounded-lg tablet:text-sm tablet:min-h-[42px] tablet:rounded-lg px-3 text-black"
                />
                <button
                  onClick={sendMessage}
                  disabled={!isConnected || !inputMessage.trim()} 
                  className="px-5 py-3 bg-gradient-to-br from-[#344450] to-[#4B6273] text-white border-none rounded-xl text-sm font-medium cursor-pointer transition-all duration-300 min-w-[80px] mb-0.5 hover:not(:disabled):-translate-y-0.5 hover:not(:disabled):shadow-[0_5px_15px_rgba(102,126,234,0.3)] disabled:bg-disabled disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none xs:px-3 xs:py-2.5 xs:text-xs xs:min-w-[60px] xs:rounded-lg tablet:px-4 tablet:py-2.5 tablet:text-sm tablet:min-w-[70px] tablet:rounded-lg"
                >
                  {t("send")} 
                </button>
              </div>

              {/* Text Mode Hint */}
              {/* {isConnected && (
                <div className="mt-3 text-center xs:mt-2 tablet:mt-2.5">
                  <p className="text-disabled text-sm m-0 xs:text-xs tablet:text-sm">
                    ⌨️ Text mode active. Type your message and press Enter to
                    send.
                  </p>
                </div>
              )} */}
            </div>
          </div>
      )}

      {/* Floating Chat Button */}
      <button
        className="w-16 h-16 md:w-15 md:h-15 rounded-full bg-gradient-to-br from-oeaMain to-[#76B2CC] border-none text-white cursor-pointer shadow-[0_4px_20px_rgba(52,68,80,0.4)] transition-all duration-300 flex items-center justify-center hover:-translate-y-0.5 hover:scale-105 hover:shadow-[0_6px_25px_rgba(52,68,80,0.6)] active:translate-y-0 active:scale-95 xs:w-12 xs:h-12 xs:shadow-[0_3px_15px_rgba(52,68,80,0.4)] tablet:w-14 tablet:h-14 tablet:shadow-[0_3px_18px_rgba(52,68,80,0.4)]"
        onClick={async () => {
          const willOpen = !isChatOpen;
          setIsChatOpen(willOpen);
          if (!willOpen) {
            // Automatically end conversation when closing chat
            if (isConnected) {
              await endConversation();
            }
          }
        }}
        aria-label="Toggle AI Chat"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="xs:w-5 xs:h-5 tablet:w-6 tablet:h-6"
        >
          <path
            d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
            fill="currentColor"
          />
        </svg>
      </button>
    </div>
  );
};

export default AIChat;
